import { Injectable } from '@angular/core';
import { Observable, shareReplay, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** 
 * One entry = one cached answer (as an Observable) plus the set of 
 * "sticky notes" (tags) describing what it depends on that decide when to drop it. */
interface PimCacheEntry {
  response$: Observable<any>;
  tags: Set<string>;
}

/**
 * ============================================================================
 *  PimCacheService — the store behind the step-response cache
 * ============================================================================
 * One map and nothing clever: `entries`, key -> the shared response plus the
 * tags it was built from.
 *
 * Dirtying a tag walks that map instead of keeping a tag -> keys index beside
 * it. The store holds one entry per distinct request within a single station's
 * locked session — tens, not thousands — and it is emptied when the lock goes,
 * so the walk costs less than the bookkeeping an index needs to stay in step
 * with it. Walking is also correct for free when several entries carry the same
 * tag, which every station-scoped read does. Which tags mean what is decided
 * next door in pim-cache.policy.ts — this class only files things under them.
 *
 * ARMING. The cache does nothing at all until `arm()` is called, and `arm()` is
 * called from exactly one place: StationLockService, the moment the server
 * confirms this session holds the draft's edit lock. That is not a detail of
 * the wiring, it is the whole safety argument. Keeping a response is a bet that
 * nothing has changed it since it was read; the lock is what makes that bet
 * safe, because it guarantees there is no second editor. No lock, no bet — a
 * failed lock call, a draft that does not exist yet, or the
 * `singleEditorSession` capability switched off all leave this class inert and
 * every read going to the server.
 *
 * `disarm()` on the way out empties the store, so a session's cached rows never
 * outlive the lock that made them safe to keep.
 *
 * It holds the OBSERVABLE rather than the value, which also means two steps
 * asking for the same rows in the same tick share one request instead of
 * racing. A failed request drops itself, so one dropped connection cannot
 * leave a step permanently broken for the rest of the visit.
 */
@Injectable({ providedIn: 'root' })
export class PimCacheService {
  private readonly entries = new Map<string, PimCacheEntry>();

  /** True only between `arm()` and `disarm()`. Nothing is served or stored otherwise. */
  private armed = false;

  /**
   * The edit lock has been confirmed by the server — caching may begin.
   *
   * Starts from empty on purpose. Anything a previous visit kept was taken
   * under a different lock, and re-reading the server once per locked session
   * is the price of never opening a draft on yesterday's data.
   */
  arm(): void {
    this.clear();
    this.armed = true;
  }

  /** The lock is gone — released, lost, or never taken. So are the rows it protected. */
  disarm(): void {
    this.armed = false;
    this.clear();
  }

  /** Whether anything may be served or stored right now. */
  isArmed(): boolean {
    return this.armed;
  }

  /**
   * Serves `key` from the cache, or runs `request()` once and keeps it under
   * `tags`.
   */
  through<T>(key: string, tags: string[], request: () => Observable<T>): Observable<T> {
    const hit = this.entries.get(key);
    if (hit) return hit.response$;
    /**
     * read() this builds (but, does not yet run) the real HTTP 
     * Observable — then wrap it with the catchError and shareReplay
     */
    const response$ = request().pipe(
      catchError((error) => {
        // a failed request should behaves as if it was never cached at all
        this.entries.delete(key);
        return throwError(() => error);
      }),
      /**
       * refCount controls whether the shared subscription destroys itself down once every subscriber 
       * has gone away (e.g. the component that asked was destroyed). false means: keep the stored 
       * answer alive even after nobody is listening anymore. This is exactly what lets a cached 
       * response survive a step component being destroyed when you click to a different step — with 
       * refCount: true, the moment the Tags step is destroyed (which is every time you leave it), the 
       * cached answer would be thrown away right along with it, and the whole feature would do nothing.
       */
      shareReplay({ bufferSize: 1, refCount: false })
    );
    /**
     * Store the entry immediately, before anyone has subscribed to it, before any 
     * network call has actually fired because if a second caller asks for the same 
     * key a moment later — even before the first real HTTP call has finished — 
     * entries.get(key) at the top of this function will already find this 
     * entry and return answer straight away.
     */
    this.entries.set(key, { response$, tags: new Set() });
    this.tag(key, tags);
    return response$;
  }

  /**
   * Files an existing entry under more tags
   * 
   * It's still called twice for one cache entry over its life: once from inside through() with the 
   * tags known up front, and again later (from the interceptor) with any tags only knowable once the 
   * response body arrived
   * 
   */
  tag(key: string, tags: string[]): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (const tag of tags) {
      if (tag) entry.tags.add(tag);
    }
  }

  /** Drops everything that depends on any of these tags. */
  invalidate(tags: string[]): void {
    for (const [key, entry] of this.entries) {
      if (tags.some((tag) => entry.tags.has(tag))) this.entries.delete(key);
    }
  }

  /** Empties the cache — an unrecognised save, or the lock going away. */
  clear(): void {
    this.entries.clear();
  }
}
